import React, { useMemo, Fragment, useState, useEffect, useContext, useRef } from "react";
import { ApolloClient, InMemoryCache, ApolloProvider, from, ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import PropTypes from "prop-types";
import Toast from "../Toast";
import { TOAST_LIST } from "../../config/toast.config";
import { MESSAGE_CONFIG } from "../../config/messages-config";
import { generateClientLinks } from "../../utils/convertor";
import { ModalContext } from "../../context/modalContext";

const stage = process.env.REACT_APP_STAGE;
const apiHost = process.env.REACT_APP_API_HOST;
const noorAIHost = process.env.REACT_APP_NOOR_AI_HOST;

const clientLinkMap = generateClientLinks(stage, apiHost, noorAIHost);

const createSplitLink = (clientLinkMapping) => {
    return new ApolloLink((operation, forward) => {
        const link = clientLinkMapping[operation.getContext().clientName];
        return link.request(operation, forward);
    });
};

const defaultOptions = {
    watchQuery: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
    },
    query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
    },
    mutate: {
        errorPolicy: "all",
    },
};

const CustomApolloProvider = ({ children }) => {
    const [toastList, setToastList] = useState([]);
    const [toastMsg, setToastMsg] = useState("");
    const { openModal } = useContext(ModalContext);

    // Use ref to always have the latest openModal function
    const openModalRef = useRef(openModal);

    useEffect(() => {
        openModalRef.current = openModal;
    }, [openModal]);

    // Helper function to check if error indicates session expiry/unauthorized
    const isSessionExpiredError = (message, code) => {
        const sessionExpiredPatterns = [
            /token.*expired/i,
            /session.*expired/i,
            /jwt.*expired/i,
            /invalid.*token/i,
            /expired.*token/i,
            /authentication.*required/i,
            /not.*authenticated/i,
            /unauthenticated/i,
        ];

        const unauthorizedCodes = [
            "UNAUTHENTICATED",
            "TOKEN_EXPIRED",
            "JWT_EXPIRED",
            "INVALID_TOKEN",
            "SESSION_EXPIRED",
        ];

        if (unauthorizedCodes.includes(code)) {
            return true;
        }

        return sessionExpiredPatterns.some((pattern) => pattern.test(message));
    };

    const isForbiddenError = (message, code) => {
        const forbiddenPatterns = [/forbidden/i, /access.*denied/i, /not.*authorized/i];

        const forbiddenCodes = ["FORBIDDEN", "UNAUTHORIZED", "ACCESS_DENIED"];

        if (forbiddenCodes.includes(code)) {
            return true;
        }

        return forbiddenPatterns.some((pattern) => pattern.test(message));
    };

    const errorLink = useMemo(
        () =>
            onError(({ graphQLErrors, networkError, response }) => {
                if (graphQLErrors) {
                    for (const err of graphQLErrors) {
                        const { message, locations, path, extensions } = err;
                        const code = extensions?.code || "";

                        console.log(
                            `[GraphQL error]: Message: ${message}, Code: ${code}, Location: ${locations}, Path: ${path}`,
                        );

                        // Check for session expired / unauthenticated errors
                        if (isSessionExpiredError(message, code)) {
                            console.log("🔒 Token expired - showing logout alert");
                            openModalRef.current("logoutAlert");
                            return; // Don't process further for this error
                        }

                        // Check for forbidden / unauthorized access errors
                        if (isForbiddenError(message, code)) {
                            console.log("🚫 Unauthorized access - showing unauthorized alert");
                            openModalRef.current("unauthorizedAlert");
                            return; // Don't process further for this error
                        }

                        // Default: show toast with user-friendly message
                        const userFriendlyMessage = MESSAGE_CONFIG[message] || message;
                        setToastMsg(userFriendlyMessage);
                    }
                }

                // Handle network errors (including 401/403 HTTP status)
                if (networkError) {
                    console.log(`[Network error]: ${networkError}`);

                    // Check for HTTP 401 (Unauthorized) or 403 (Forbidden)
                    const statusCode = networkError.statusCode || networkError.response?.status;
                    if (statusCode === 401) {
                        console.log("🔒 HTTP 401 - showing logout alert");
                        openModalRef.current("logoutAlert");
                    } else if (statusCode === 403) {
                        console.log("🚫 HTTP 403 - showing unauthorized alert");
                        openModalRef.current("unauthorizedAlert");
                    }
                }
            }),
        [],
    );

    const apolloClient = useMemo(
        () =>
            new ApolloClient({
                // The `from` function combines an array of individual links
                // into a link chain
                link: from([errorLink, createSplitLink(clientLinkMap)]),
                cache: new InMemoryCache(),
                defaultOptions,
            }),
        [errorLink],
    );

    useEffect(() => {
        if (toastMsg) {
            setToastList([
                {
                    ...TOAST_LIST.danger,
                    title: MESSAGE_CONFIG[toastMsg] ? MESSAGE_CONFIG[toastMsg] : toastMsg,
                },
            ]);
            setToastMsg(null);
        }
    }, [toastMsg]);

    return useMemo(() => {
        return (
            <Fragment>
                <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
                <Toast
                    toastList={toastList}
                    position={"top-right"}
                    autoDelete={true}
                    dismissTime={parseInt(5000, 10)}
                />
            </Fragment>
        );
    }, [toastList]);
};

CustomApolloProvider.propTypes = {
    children: PropTypes.node,
};

export default CustomApolloProvider;
